import { useState } from 'react'
import { subscribeToAlerts } from '../lib/api'

/**
 * Post-submit handoff screen — brand-aligned redesign (2026-05-04).
 *
 * Single page, no scrolling. The page exists for ONE reason: get the
 * reference code into the user's hands before they leave. Per Ryan's
 * feedback ("doesn't feel clean like it used to"):
 *
 *   - Removed: gear icon, Google sign-in section, the "—or—" divider,
 *     long privacy subtitles on each save option.
 *   - Kept: success confirmation, the code itself (big), three save
 *     paths (compact icon-button row), an optional one-line alerts
 *     subscription. All centered. All under one viewport.
 *
 * Visual language follows production tokens (Augusta-deep-green ink,
 * pure-white surfaces, DM Serif Display headline, gold hairline accent).
 */

interface PostSubmitProps {
  referenceCode: string
  /** Reserved — was used by the now-removed Google one-tap binding. */
  itemId?: string
  /**
   * Email the user provided in the submit-confirm modal. When set,
   * the post-submit screen renders a "We just emailed this to ___"
   * confirmation under the code so the user knows the email-as-
   * credential send actually happened. Per round-10 critic P2 #1:
   * "the post-submit screen never tells the user 'we just emailed
   * your code to you@example.com,' so the email-as-credential lift
   * is silently delivered instead of earned in front of the user."
   */
  submittedEmail?: string
}

const SMS_BODY = (code: string) =>
  `My Lost Club report code: ${code} — track it at https://lostblank.com/#/status/${code}`

const EMAIL_SUBJECT = (code: string) => `Your Lost Club report — ${code}`
const EMAIL_BODY = (code: string) =>
  `Your reference code is ${code}.\n\nTrack your report status here:\nhttps://lostblank.com/#/status/${code}\n\n— The Lost Club`

function isProbablyEmail(s: string) {
  return /@/.test(s) && /\.\w{2,}/.test(s)
}

export default function PostSubmit({ referenceCode, submittedEmail }: PostSubmitProps) {
  const [contact, setContact] = useState('')
  const [subscribing, setSubscribing] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [subscribeError, setSubscribeError] = useState<string>()
  const [screenshotHint, setScreenshotHint] = useState(false)
  // Copy button removed — long-press / OS clipboard works fine for the
  // small set of people who actually want to copy text-as-text. Per Ryan:
  // "remove the copy button — no one will use it."

  const handleSubscribe = async () => {
    const trimmed = contact.trim()
    if (!trimmed || subscribing) return
    setSubscribing(true)
    setSubscribeError(undefined)
    try {
      const payload = isProbablyEmail(trimmed)
        ? { email: trimmed }
        : { phone: trimmed }
      await subscribeToAlerts(referenceCode, payload)
      setSubscribed(true)
    } catch {
      setSubscribeError("Couldn't subscribe — try again in a moment.")
    } finally {
      setSubscribing(false)
    }
  }

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-8 animate-fade-in">
      <div className="w-full max-w-sm flex flex-col items-center text-center gap-5">
        {/* Success pill — quiet confirmation that submission landed.
            Round-10: consumes the eyebrow utility class instead of
            inlining font-size/weight/tracking. */}
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 border eyebrow-token uppercase"
          style={{
            borderColor: 'var(--border-default)',
            color: 'var(--ink-2)',
            borderRadius: 999,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Report submitted
        </div>

        {/* Headline — gold hairline + serif imperative. */}
        <div className="flex flex-col items-center gap-2">
          <div style={{ width: 24, height: 2, background: 'var(--gold)' }} />
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 500,
              fontSize: '32px',
              lineHeight: 1.05,
              letterSpacing: '-0.015em',
              color: 'var(--ink-1)',
              margin: 0,
            }}
          >
            Keep this nearby.
          </h1>
        </div>

        {/* The reference code — monospace, demoted from 36/700 to 24/600
            per the 2026-05-06 critic audit. E2: "reference codes are
            monospace; the serif treatment makes it look like a poem,
            not a credential the user must save." E1: "the 36px mono
            outweighs the 32px serif headline above it — invert the
            scale." Now sits visually below the headline as a credential
            (a tag, not a hero). No Copy button (per Ryan: "no one will
            use it" — long-press / OS native copy already works). */}
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '28px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            color: 'var(--ink-1)',
          }}
        >
          {referenceCode}
        </span>

        {/* Copy-to-clipboard + the conditional email-backed confirmation.
            When the user provided an email in the submit modal, the line
            beneath the code says "Sent to you@example.com — you're
            backed up." instead of the dry "Without it, you can't check
            status…" warning. Per round-10 critic P2 #1: "render one
            line under the code: 'We just emailed this to you@example.com.
            You're backed up.' Worth ~+0.3 on its own." */}
        <CopyCodeLink referenceCode={referenceCode} />

        {submittedEmail ? (
          <p
            className="max-w-[32ch] animate-fade-in"
            style={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'var(--accent)',
              margin: 0,
              fontStyle: 'italic',
              fontFamily: 'var(--font-serif)',
            }}
          >
            Also sent to <span style={{ fontStyle: 'normal', fontFamily: 'var(--font-sans)', fontWeight: 500 }}>{submittedEmail}</span>. You&rsquo;re backed up.
          </p>
        ) : (
          <p
            className="max-w-[28ch]"
            style={{
              fontSize: '13px',
              lineHeight: 1.5,
              color: 'var(--ink-3)',
              margin: 0,
            }}
          >
            Without it, you can't check status or update your report.
          </p>
        )}

        {/* Save row — three compact icon-buttons inline. The header
            spells out plainly that these send the code to the user
            themselves; we don't see, store, or use any of it. */}
        <p
          className="eyebrow-token uppercase mt-2"
          style={{
            fontFamily: 'var(--font-sans)',
            color: 'var(--accent)',
          }}
        >
          Send it to yourself
        </p>
        <p
          className="text-[11px] -mt-3 max-w-[30ch]"
          style={{ color: 'var(--ink-3)', lineHeight: 1.45 }}
        >
          Goes to your own phone or inbox — we never see it, store it, or use it.
        </p>
        <div className="flex gap-2 w-full">
          <a
            href={`sms:?&body=${encodeURIComponent(SMS_BODY(referenceCode))}`}
            className="flex-1 flex flex-col items-center gap-1 py-2.5 border transition-colors hover:bg-[var(--surface-2)]"
            style={{ borderColor: 'var(--border-default)', borderRadius: 0, color: 'var(--ink-1)' }}
            aria-label="Text the code to yourself"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="text-[11px] font-medium">Text</span>
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT(referenceCode))}&body=${encodeURIComponent(EMAIL_BODY(referenceCode))}`}
            className="flex-1 flex flex-col items-center gap-1 py-2.5 border transition-colors hover:bg-[var(--surface-2)]"
            style={{ borderColor: 'var(--border-default)', borderRadius: 0, color: 'var(--ink-1)' }}
            aria-label="Email the code to yourself"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            <span className="text-[11px] font-medium">Email</span>
          </a>
          <button
            onClick={() => setScreenshotHint(true)}
            className="flex-1 flex flex-col items-center gap-1 py-2.5 border transition-colors hover:bg-[var(--surface-2)] text-left"
            style={{ borderColor: 'var(--border-default)', borderRadius: 0, color: 'var(--ink-1)' }}
            aria-label="Take a screenshot of the code"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <span className="text-[11px] font-medium">Screenshot</span>
          </button>
        </div>
        {screenshotHint && (
          <p
            className="text-[11px] italic -mt-3"
            style={{ color: 'var(--ink-3)' }}
            aria-live="polite"
          >
            Snap it now — the code is right above ↑
          </p>
        )}

        {/* Optional alerts — single inline row. Phone OR email,
            auto-detected. We never share or sell this. */}
        <div className="w-full pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          {subscribed ? (
            <div
              className="px-3 py-2 text-[12px]"
              style={{
                color: 'var(--accent-deep)',
                background: 'var(--accent-subtle)',
                borderRadius: 0,
              }}
            >
              ✓ We'll text or email you the moment we match it.
            </div>
          ) : (
            <>
              <p
                className="eyebrow-token uppercase mb-1"
                style={{
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--ink-3)',
                }}
              >
                Optional · Match alerts
              </p>
              <p className="text-[11px] mb-2" style={{ color: 'var(--ink-3)', lineHeight: 1.45 }}>
                Only if you want a ping the moment we match it. We never share or sell.
              </p>
              <div className="flex gap-2 w-full">
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
                  placeholder="Phone or email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="flex-1 px-3 py-2 border focus:outline-none focus:border-[var(--border-accent)]"
                  style={{
                    borderColor: 'var(--border-default)',
                    background: 'var(--surface-1)',
                    color: 'var(--ink-1)',
                    fontSize: '13px',
                    borderRadius: 0,
                  }}
                  aria-label="Phone number or email address for match notifications"
                />
                <button
                  onClick={handleSubscribe}
                  disabled={!contact.trim() || subscribing}
                  className="px-3 py-2 text-[11px] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                  style={{
                    background: 'var(--accent)',
                    color: 'var(--text-on-accent)',
                    letterSpacing: '0.14em',
                    borderRadius: 0,
                    border: 0,
                  }}
                >
                  {subscribing ? '…' : 'Notify me'}
                </button>
              </div>
              {subscribeError && (
                <p className="mt-1 text-[11px]" style={{ color: 'var(--error)' }}>
                  {subscribeError}
                </p>
              )}
            </>
          )}
        </div>

        {/* View report link — explicit way to navigate to the status page
            for this code. The footer of the page; quietly available, not
            an auto-redirect. */}
        <a
          href={`#/status/${referenceCode}`}
          className="text-[13px] hover:underline"
          style={{ color: 'var(--accent)', fontWeight: 500 }}
        >
          View your report →
        </a>
      </div>
    </div>
  )
}

/**
 * CopyCodeLink — quiet "copy code" affordance under the reference
 * credential. Round-10 P1 fix: post-submit momentum kit. Caps-tracked
 * underline link, not a pill. Flips to "Copied ↩" for 1.6s on success.
 * Falls back gracefully if Clipboard API isn't available.
 */
function CopyCodeLink({ referenceCode }: { referenceCode: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referenceCode)
      } else {
        // Legacy fallback — create a hidden textarea and execCommand.
        const ta = document.createElement('textarea')
        ta.value = referenceCode
        ta.setAttribute('readonly', '')
        ta.style.position = 'absolute'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch (err) {
      console.error('copy failed:', err)
    }
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="eyebrow-token-sm uppercase hover:underline transition-colors"
      style={{
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        cursor: 'pointer',
        color: copied ? 'var(--accent)' : 'var(--ink-3)',
      }}
      aria-label="Copy reference code to clipboard"
    >
      {copied ? 'Copied ↩' : 'Copy code'}
    </button>
  )
}
